import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Field';
import { CITIES } from '@/config/platform';
import { ROUTES } from '@/config/navigation';
import { toDateInputValue } from '@/lib/dates';
import { useCities } from '@/features/platform/queries';
import { criteriaToSearchParams, type SearchCriteria } from './criteria';

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

/**
 * Moteur de recherche principal.
 *
 * Les villes viennent de la base ; la liste du fichier de configuration ne sert
 * que de repli quand Supabase n'est pas encore branché.
 */
export function SearchForm({
  compact = false,
  initial,
}: {
  compact?: boolean;
  initial?: Partial<SearchCriteria>;
}) {
  const navigate = useNavigate();
  const today = new Date();
  const citiesQuery = useCities();

  const cities =
    citiesQuery.data?.map((city) => ({
      slug: city.slug,
      label: city.name,
      active: city.is_active,
    })) ?? CITIES.map((city) => ({ slug: city.slug, label: city.label, active: city.active }));

  const [city, setCity] = useState(initial?.lieu ?? 'abidjan');
  const [startDate, setStartDate] = useState(
    initial?.depart ?? toDateInputValue(addDays(today, 1)),
  );
  const [startTime, setStartTime] = useState(initial?.heureDepart ?? '10:00');
  const [endDate, setEndDate] = useState(initial?.retour ?? toDateInputValue(addDays(today, 4)));
  const [endTime, setEndTime] = useState(initial?.heureRetour ?? '10:00');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${endDate}T${endTime}`);

    if (end <= start) {
      setError('La date de retour doit être postérieure à la date de départ.');
      return;
    }
    setError(null);

    const params = criteriaToSearchParams({
      ...initial,
      lieu: city,
      depart: startDate,
      heureDepart: startTime,
      retour: endDate,
      heureRetour: endTime,
      page: 1,
    });

    navigate(`${ROUTES.search}?${params.toString()}`);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={
        // Grille fluide : les champs se répartissent d'eux-mêmes, et le bouton
        // reste sur la même ligne tant que la place le permet. Aucune rupture
        // à surveiller, aucun point de bascule à maintenir.
        compact
          ? 'grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]'
          : 'grid gap-3.5 rounded-2xl border border-ink-200 bg-white p-4 shadow-card [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]'
      }
    >
      <div>
        <Select
          label="Lieu de prise en charge"
          value={city}
          onChange={(e) => setCity(e.target.value)}
        >
          {cities.map((option) => (
            <option key={option.slug} value={option.slug} disabled={!option.active}>
              {option.label}
              {option.active ? '' : ' — bientôt'}
            </option>
          ))}
        </Select>
      </div>

      <Input
        label="Date de départ"
        type="date"
        value={startDate}
        min={toDateInputValue(today)}
        onChange={(e) => setStartDate(e.target.value)}
      />
      <Input
        label="Heure"
        type="time"
        value={startTime}
        onChange={(e) => setStartTime(e.target.value)}
      />
      <Input
        label="Date de retour"
        type="date"
        value={endDate}
        min={startDate}
        onChange={(e) => setEndDate(e.target.value)}
      />
      <Input
        label="Heure"
        type="time"
        value={endTime}
        onChange={(e) => setEndTime(e.target.value)}
      />

      {/* Sur mobile la grille tombe a deux colonnes : le bouton prend toute
          la largeur plutot que de se coller a un champ d'heure. */}
      <div className="flex items-end [grid-column:1/-1] sm:[grid-column:auto]">
        <Button type="submit" size="lg" className="w-full">
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor">
            <circle cx="9" cy="9" r="6" strokeWidth="2" />
            <path d="M13.5 13.5L18 18" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Rechercher
        </Button>
      </div>

      {error && (
        <p className="text-sm text-red-600 [grid-column:1/-1]" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
